import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Banner from './Banner';
import StarRating from './StarRating';
import OpenStatus from './OpenStatus';
import { useUser } from './UserContext';
import { getFirestore, getDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import placesData from './data/SK.json';
import './css/Featured.css';

function Featured() {
  const { user } = useUser();
  const db = getFirestore();
  const [favorites, setFavorites] = useState([]);
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [featuredPlaces, setFeaturedPlaces] = useState([]);
  const gridRef = useRef(null); // Ref for the product grid


  // Fetch user favorites
  useEffect(() => {
    if (!user) return;
    const fetchFavorites = async () => {
      const userRef = doc(db, 'Users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setFavorites(userSnap.data().favorites || []);
      }
    };
    fetchFavorites();
  }, [user, db]);

  // Handle favorite toggle
  const handleFavorite = async (placeId) => {
    if (!user) {
      alert('You must be logged in to favorite a place.');
      return;
    }

    const userRef = doc(db, 'Users', user.uid);
    if (favorites.includes(placeId)) {
      await updateDoc(userRef, { favorites: arrayRemove(placeId) });
      setFavorites(favorites.filter((id) => id !== placeId));
      setPopupMessage('Vymazané z obľúbených');
    } else {
      await updateDoc(userRef, { favorites: arrayUnion(placeId) });
      setFavorites([...favorites, placeId]);
      setPopupMessage('Pridané do obľúbených!');
    }

    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2000);
  };

  // Pick 3 random premium places from json
  useEffect(() => {
    const premiumPlaces = placesData.filter((p) => p.premium === '1');
    const shuffled = premiumPlaces.sort(() => 0.5 - Math.random());
    setFeaturedPlaces(shuffled.slice(0, 3));

  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    let scrollAmount = 0;
    const scrollStep = 1; // pixels per interval
    const scrollInterval = 20; // ms

    const interval = setInterval(() => {
      if (grid.scrollWidth - grid.clientWidth <= scrollAmount) {
        scrollAmount = 0; // reset to start
        grid.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollAmount += scrollStep;
        grid.scrollTo({ left: scrollAmount });
      }
    }, scrollInterval);

    return () => clearInterval(interval);
  }, [featuredPlaces]);


  return (
    <>
      <Header />
      <Banner />

      <h2 className="rec">Odporúčame</h2>

      <div className="product-grid" ref={gridRef}>
        {featuredPlaces.map((place) => (
          <div
            className={`products-tile`}
            key={place.place_id}
            style={{ position: 'relative' }}
          >
            <div className="recommended-box">
              <i className="fa-solid fa-check"></i> Overený užívateľ
            </div>

            <div
              className="tile-image"
              style={{
                backgroundImage: `url(${place.short_photo_references && place.short_photo_references.length > 0
                  ? `/images/${place.short_photo_references[0]}.jpg`
                  : `/images/default.png`})`,
              }}
            ></div>

            <div className="tile-content">
              <h2>
                <Link to={`/catalog/${place.place_id}`}>
                  {place.name.length > 40 ? `${place.name.substring(0, 40)}...` : place.name}
                </Link>
              </h2>
              <p>
                {place.address.length > 60 ? `${place.address.substring(0, 60)}...` : place.address}
              </p>

              <div className="place-rating">
                <StarRating rating={place.rating || 0} />
                <p>Hodnotilo: {place.user_ratings_total || 0}</p>
              </div>
              <OpenStatus openingHours={place.opening_hours} />
            </div>



          </div>

        ))}
      </div>

      {showPopup && <div className="popup-message">{popupMessage}</div>}



      <div className="categories-container">
        <div>
          <h2 className="font-bold text-lg mb-2">Krása</h2>
          <Link to="/catalog?search=kadernictvo" className="category-link block">Kaderníctvo</Link>
          <Link to="/catalog?search=kozmetika" className="category-link block">Kozmetika</Link>
          <Link to="/catalog?search=manikura%20pedikura" className="category-link block">Manikúra & Pedikúra</Link>
          <Link to="/catalog?search=solarium" className="category-link block">Solárium</Link>
        </div>

        <div>
          <h2 className="font-bold text-lg mb-2">Zdravie</h2>
          <Link to="/catalog?search=fitness" className="category-link block">Fitness</Link>
          <Link to="/catalog?search=fyzioterapia" className="category-link block">Fyzioterapia</Link>
          <Link to="/catalog?search=masaze" className="category-link block">Masáže</Link>
          <Link to="/catalog?search=yoga" className="category-link block">Joga</Link>
          <Link to="/catalog?search=pilates" className="category-link block">Pilates</Link>
        </div>

        <div>
          <h2 className="font-bold text-lg mb-2">Auto</h2>
          <Link to="/catalog?search=autoservis" className="category-link block">Autoservis</Link>
          <Link to="/catalog?search=umyvarka" className="category-link block">Ručná umyváreň</Link>
          <Link to="/catalog?search=pneuservis" className="category-link block">Pneuservis</Link>
          <Link to="/catalog?search=detailing" className="category-link block">Detailing</Link>
          <Link to="/catalog?search=odtahova%20sluzba" className="category-link block">Odťahová Služba</Link>
        </div>

        <div>
          <h2 className="font-bold text-lg mb-2">Bývanie</h2>
          <Link to="/catalog?search=vodar" className="category-link block">Vodár</Link>
          <Link to="/catalog?search=klucova%20sluzba" className="category-link block">Kľúčová služba</Link>
          <Link to="/catalog?search=domaci%20majster" className="category-link block">Hodinový manžel</Link>
          <Link to="/catalog?search=elektrikar" className="category-link block">Elektrikár</Link>
          <Link to="/catalog?search=biela%20technika" className="category-link block">Servis Biela Technika</Link>
        </div>

        <div>
          <h2 className="font-bold text-lg mb-2">Remeslá</h2>
          <Link to="/catalog?search=stolar" className="category-link block">Stolár</Link>
          <Link to="/catalog?search=podlahar" className="category-link block">Podlahár</Link>
          <Link to="/catalog?search=maliar" className="category-link block">Maliar</Link>
          <Link to="/catalog?search=obkladac" className="category-link block">Obkladač</Link>
          <Link to="/catalog?search=sadrokarton" className="category-link block">Sadrokartonista</Link>
        </div>

        <div>
          <h2 className="font-bold text-lg mb-2">Osobné služby</h2>
          <Link to="/catalog?search=cukrar" className="category-link block">Cukrár</Link>
          <Link to="/catalog?search=doucovanie" className="category-link block">Doučovanie</Link>
          <Link to="/catalog?search=fotograf" className="category-link block">Fotograf</Link>
          <Link to="/catalog?search=kameraman" className="category-link block">Kameraman</Link>
          <Link to="/catalog?search=tlmocnik%20prekladatel" className="category-link block">Tlmočník & Prekladateľ</Link>
        </div>
      </div>


    </>
  );
}

export default Featured;
